import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPencil,
  faTrash,
  faDollarSign,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

const WashingCostsManagement = () => {
  const currentYear = new Date().getFullYear();
  const [washingCosts, setWashingCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySourceYear, setCopySourceYear] = useState(null);
  const [copyTargetYear, setCopyTargetYear] = useState("");
  const [copyError, setCopyError] = useState("");
  const [copying, setCopying] = useState(false);
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [availableYears, setAvailableYears] = useState([]);
  const [formData, setFormData] = useState({
    procedure_name: "",
    cost_per_unit_cad: "",
    currency: "CAD",
    unit_of_measure: "sqm",
    year: currentYear,
    sort_order: 99,
    is_default: false,
    is_active: true,
  });
  const [convertedCost, setConvertedCost] = useState(null);
  const [defaultSystemSettings, setDefaultSystemSettings] = useState({
    currency: "CAD",
    uom: "sqm",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState("");

  const sortBySortOrder = (items = []) =>
    [...items].sort((a, b) => {
      const aSort = Number(a.sortOrder ?? a.sort_order ?? 99);
      const bSort = Number(b.sortOrder ?? b.sort_order ?? 99);
      if (aSort !== bSort) return aSort - bSort;

      const aName = String(a.procedureName || a.procedure_name || "");
      const bName = String(b.procedureName || b.procedure_name || "");
      return aName.localeCompare(bName);
    });

  // Fetch default system settings
  useEffect(() => {
    const fetchDefaultSettings = async () => {
      try {
        const settingsResponse = await api.get("/settings/categories");
        if (settingsResponse.data.success) {
          const settings = settingsResponse.data.data;
          const currency =
            settings.currency_units?.currency?.setting_value || "CAD";
          const preferredUnits =
            settings.currency_units?.preferred_units?.setting_value || "m²";
          const uom = preferredUnits.toLowerCase().includes("m")
            ? "sqm"
            : "sqft";
          setDefaultSystemSettings({ currency, uom });
        }
      } catch (err) {
        console.error("Error fetching default settings:", err);
      }
    };
    fetchDefaultSettings();
  }, []);

  // Calculate converted cost when form data changes
  useEffect(() => {
    const calculateConvertedCost = async () => {
      if (
        !formData.cost_per_unit_cad ||
        !formData.currency ||
        !formData.unit_of_measure ||
        !formData.year
      ) {
        setConvertedCost(null);
        return;
      }

      try {
        // Get conversion rate for the year
        let currencyRate = 1.0;
        if (formData.currency !== defaultSystemSettings.currency) {
          const rateResponse = await api.get(
            `/currency-conversion-rates/year/${formData.year}/${formData.currency}/${defaultSystemSettings.currency}`,
          );
          if (rateResponse.data.success) {
            currencyRate = parseFloat(rateResponse.data.data.conversionRate);
          }
        }

        // Convert currency
        let converted = parseFloat(formData.cost_per_unit_cad) * currencyRate;

        // Convert UOM (1 sqft = 0.092903 sqm)
        const SQFT_TO_SQM = 0.092903;
        if (
          formData.unit_of_measure === "sqft" &&
          defaultSystemSettings.uom === "sqm"
        ) {
          converted = converted / SQFT_TO_SQM;
        } else if (
          formData.unit_of_measure === "sqm" &&
          defaultSystemSettings.uom === "sqft"
        ) {
          converted = converted * SQFT_TO_SQM;
        }

        setConvertedCost(converted.toFixed(2));
      } catch (err) {
        console.error("Error calculating converted cost:", err);
        setConvertedCost(null);
      }
    };

    calculateConvertedCost();
  }, [
    formData.cost_per_unit_cad,
    formData.currency,
    formData.unit_of_measure,
    formData.year,
    defaultSystemSettings,
  ]);

  // Fetch washing costs grouped by year
  const fetchWashingCosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        grouped_by_year: "true",
        ...(searchTerm && { search: searchTerm }),
        ...(yearFilter && { year: yearFilter }),
      });

      const response = await api.get(`/washing-costs?${params}`);
      if (response.data.success) {
        const data = response.data.data || [];
        setWashingCosts(data);
        setExpandedYears((prev) => {
          if (data.length > 0 && prev.size === 0) {
            return new Set([data[0].year]);
          }
          return prev;
        });
      }
    } catch (err) {
      setError("Failed to fetch washing costs");
      console.error("Error fetching washing costs:", err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, yearFilter]);

  const fetchAvailableYears = useCallback(async () => {
    try {
      const response = await api.get("/washing-costs?grouped_by_year=true");
      if (response.data.success) {
        const years = Array.from(
          new Set(
            (response.data.data || [])
              .map((g) => Number(g.year))
              .filter((y) => Number.isFinite(y)),
          ),
        ).sort((a, b) => b - a);
        setAvailableYears(years);
      }
    } catch (err) {
      console.error("Error fetching washing cost years:", err);
    }
  }, []);

  useEffect(() => {
    fetchWashingCosts();
    fetchAvailableYears();
  }, [fetchWashingCosts, fetchAvailableYears]);

  const toggleYearExpansion = (year) => {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  const handleImportPreviousYear = async (targetYear) => {
    const previousYearData = washingCosts
      .filter((y) => y.year < targetYear)
      .sort((a, b) => b.year - a.year)[0];

    if (!previousYearData) {
      alert("No previous year data found to import.");
      return;
    }

    if (
      !window.confirm(
        `Import all washing costs from ${previousYearData.year} to ${targetYear}?`,
      )
    ) {
      return;
    }

    try {
      const importPromises = (previousYearData.costs || []).map((cost) =>
        api.post("/washing-costs", {
          procedure_name: cost.procedureName || cost.procedure_name,
          cost_per_unit_cad: Number(
            cost.costPerUnitCad ?? cost.cost_per_unit_cad ?? 0,
          ),
          currency: cost.currency || "CAD",
          unit_of_measure: cost.unitOfMeasure || cost.unit_of_measure || "sqm",
          year: targetYear,
          sort_order: Number(cost.sortOrder ?? cost.sort_order ?? 99),
          is_default: Boolean(cost.isDefault || cost.is_default),
          is_active:
            cost.isActive !== undefined
              ? cost.isActive
              : cost.is_active !== false,
        }),
      );

      const results = await Promise.allSettled(importPromises);
      const successful = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - successful;

      if (failed > 0) {
        alert(
          `Imported ${successful} of ${results.length} washing costs. ${failed} failed.`,
        );
      } else {
        alert(
          `Successfully imported ${successful} washing costs from ${previousYearData.year} to ${targetYear}.`,
        );
      }

      await fetchWashingCosts();
      await fetchAvailableYears();
    } catch (err) {
      alert(
        "Failed to import previous year washing costs: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleCopyFromYear = (sourceYear) => {
    const sourceYearData = washingCosts.find((y) => y.year === sourceYear);
    if (!sourceYearData || !(sourceYearData.costs || []).length) {
      alert(`No washing costs found in source year ${sourceYear}.`);
      return;
    }

    const usedYears = new Set(availableYears.map((y) => Number(y)));
    let suggestedYear = sourceYear + 1;
    while (usedYears.has(suggestedYear) && suggestedYear <= 3000) {
      suggestedYear += 1;
    }

    setCopySourceYear(sourceYear);
    setCopyTargetYear(String(suggestedYear));
    setCopyError("");
    setShowCopyModal(true);
  };

  const handleConfirmCopy = async () => {
    if (!copySourceYear) return;

    const targetYear = parseInt(copyTargetYear, 10);
    if (
      !Number.isInteger(targetYear) ||
      targetYear < 1900 ||
      targetYear > 3000
    ) {
      setCopyError("Please enter a valid target year.");
      return;
    }
    if (targetYear === copySourceYear) {
      setCopyError("Target year must be different from source year.");
      return;
    }
    if (availableYears.includes(targetYear)) {
      setCopyError(
        `Year ${targetYear} already exists. Please choose a new year.`,
      );
      return;
    }

    const sourceYearData = washingCosts.find((y) => y.year === copySourceYear);
    if (!sourceYearData || !(sourceYearData.costs || []).length) {
      setCopyError(`No washing costs found in source year ${copySourceYear}.`);
      return;
    }

    try {
      setCopying(true);
      setCopyError("");
      const copyPromises = (sourceYearData.costs || []).map((cost) =>
        api.post("/washing-costs", {
          procedure_name: cost.procedureName || cost.procedure_name,
          cost_per_unit_cad: Number(
            cost.costPerUnitCad ?? cost.cost_per_unit_cad ?? 0,
          ),
          currency: cost.currency || "CAD",
          unit_of_measure: cost.unitOfMeasure || cost.unit_of_measure || "sqm",
          year: targetYear,
          sort_order: Number(cost.sortOrder ?? cost.sort_order ?? 99),
          is_default: Boolean(cost.isDefault || cost.is_default),
          is_active:
            cost.isActive !== undefined
              ? cost.isActive
              : cost.is_active !== false,
        }),
      );

      const results = await Promise.allSettled(copyPromises);
      const successful = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - successful;

      if (failed > 0) {
        alert(
          `Copied ${successful} of ${results.length} washing costs. ${failed} failed.`,
        );
      } else {
        alert(
          `Successfully copied ${successful} washing costs from ${copySourceYear} to ${targetYear}.`,
        );
      }

      await fetchWashingCosts();
      await fetchAvailableYears();
      setShowCopyModal(false);
      setCopySourceYear(null);
      setCopyTargetYear("");
    } catch (err) {
      setCopyError(
        "Failed to copy washing costs: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setCopying(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCost) {
        await api.put(`/washing-costs/${editingCost.id}`, formData);
      } else {
        await api.post("/washing-costs", formData);
      }

      setShowModal(false);
      setEditingCost(null);
      resetForm();
      fetchWashingCosts();
      fetchAvailableYears();
    } catch (err) {
      setError("Failed to save washing cost");
      console.error("Error saving washing cost:", err);
    }
  };

  // Handle edit
  const handleEdit = (cost) => {
    setEditingCost(cost);
    setFormData({
      procedure_name: cost.procedureName,
      cost_per_unit_cad: cost.costPerUnitCad,
      currency: cost.currency,
      unit_of_measure: cost.unitOfMeasure,
      year: cost.year,
      sort_order: cost.sortOrder ?? cost.sort_order ?? 99,
      is_default: cost.isDefault || false,
      is_active: cost.isActive !== undefined ? cost.isActive : true,
    });
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this washing cost?")) {
      try {
        await api.delete(`/washing-costs/${id}`);
        fetchWashingCosts();
        fetchAvailableYears();
      } catch (err) {
        setError("Failed to delete washing cost");
        console.error("Error deleting washing cost:", err);
      }
    }
  };

  const handleDeleteWashingYear = async (year) => {
    const yearData = washingCosts.find((y) => y.year === year);
    const costs = (yearData && yearData.costs) || [];
    if (!costs.length) {
      alert(`No washing procedures found for ${year}.`);
      return;
    }
    if (
      !window.confirm(
        `Delete all ${costs.length} washing procedure(s) for ${year}? This cannot be undone.`,
      )
    )
      return;
    try {
      await Promise.all(
        costs.map((cost) => api.delete(`/washing-costs/${cost.id}`)),
      );
      fetchWashingCosts();
      fetchAvailableYears();
    } catch (err) {
      setError("Failed to delete washing procedures for year");
      console.error("Error deleting washing procedures for year:", err);
    }
  };

  const getCostIsActive = (cost) =>
    cost.isActive !== undefined ? cost.isActive : cost.is_active !== false;

  const handleToggleActive = async (cost) => {
    try {
      await api.put(`/washing-costs/${cost.id}`, {
        is_active: !getCostIsActive(cost),
      });
      await fetchWashingCosts();
      await fetchAvailableYears();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update active status");
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      procedure_name: "",
      cost_per_unit_cad: "",
      currency: "CAD",
      unit_of_measure: defaultSystemSettings.uom,
      year: currentYear,
      sort_order: 99,
      is_default: false,
      is_active: true,
    });
    setEditingCost(null);
    setConvertedCost(null);
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start gap-2">
        <p className="text-gray-600">
          Manage washing procedures and their costs for bridge maintenance
        </p>
        <InlineFieldTooltip
          page="washing_costs"
          section="page_description"
          fieldKey="washing_costs_overview"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search washing procedures..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Years</option>
          {availableYears.map((year) => (
            <option key={year} value={String(year)}>
              {year}
            </option>
          ))}
        </select>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
          Add Washing Cost
        </button>
      </div>

      {/* Table - Grouped by Year */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading washing costs...</p>
          </div>
        ) : washingCosts.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p>No washing costs found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Currency / UOM
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Procedures Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {washingCosts.map((yearData) => {
                  const isExpanded = expandedYears.has(yearData.year);
                  return (
                    <React.Fragment key={yearData.year}>
                      <tr className="bg-gray-50 hover:bg-gray-100">
                        <td className="px-4 py-4">
                          <button
                            type="button"
                            onClick={() => toggleYearExpansion(yearData.year)}
                            className="text-gray-500 hover:text-gray-700"
                            title={isExpanded ? "Collapse year" : "Expand year"}
                          >
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronDown : faChevronRight}
                            />
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {yearData.year}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {yearData.currency || "-"} /{" "}
                          {yearData.unitOfMeasure || "sqm"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {(yearData.costs || []).length} procedures
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          -
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                handleImportPreviousYear(yearData.year)
                              }
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-green-300 text-green-700 bg-green-50 hover:bg-green-100"
                              title={`Import previous year procedures into ${yearData.year}`}
                            >
                              Import
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCopyFromYear(yearData.year)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                              title={`Copy procedures from ${yearData.year} to a target year`}
                            >
                              Copy
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteWashingYear(yearData.year)
                              }
                              className="text-red-600 hover:text-red-900"
                              title={`Delete all washing procedures for ${yearData.year}`}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <>
                          <tr>
                            <td className="px-4 py-2 bg-gray-50"></td>
                            <td
                              colSpan="5"
                              className="px-6 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider"
                            >
                              Procedures for {yearData.year}
                            </td>
                          </tr>
                          {sortBySortOrder(yearData.costs || []).map((cost) => (
                            <tr key={cost.id} className="hover:bg-gray-50">
                              <td className="px-4 py-4"></td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      (cost.procedureName ||
                                        cost.procedure_name) ===
                                      "Default no Wash"
                                        ? "bg-gray-100 text-gray-800"
                                        : (cost.procedureName ||
                                              cost.procedure_name) === "Wash"
                                          ? "bg-blue-100 text-blue-800"
                                          : "bg-green-100 text-green-800"
                                    }`}
                                  >
                                    {cost.procedureName || cost.procedure_name}
                                  </span>
                                  {(cost.isDefault || cost.is_default) && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                      Default
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <FontAwesomeIcon
                                    icon={faDollarSign}
                                    className="h-4 w-4 text-green-600 mr-1"
                                  />
                                  <span className="font-medium">
                                    {parseFloat(
                                      cost.costPerUnitCad ??
                                        cost.cost_per_unit_cad ??
                                        0,
                                    ).toFixed(2)}{" "}
                                    {cost.currency || "CAD"} /{" "}
                                    {cost.unitOfMeasure ||
                                      cost.unit_of_measure ||
                                      "sqm"}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                Sort: {cost.sortOrder ?? cost.sort_order ?? 99}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={getCostIsActive(cost)}
                                  onClick={() => handleToggleActive(cost)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${getCostIsActive(cost) ? "bg-blue-600" : "bg-gray-300"}`}
                                  title="Toggle active status"
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${getCostIsActive(cost) ? "translate-x-6" : "translate-x-1"}`}
                                  />
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEdit(cost)}
                                    className="text-blue-600 hover:text-blue-900"
                                    title="Edit"
                                  >
                                    <FontAwesomeIcon
                                      icon={faPencil}
                                      className="h-4 w-4"
                                    />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(cost.id)}
                                    className="text-red-600 hover:text-red-900"
                                    title="Delete"
                                  >
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      className="h-4 w-4"
                                    />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                <span>
                  {editingCost ? "Edit Washing Cost" : "Add New Washing Cost"}
                </span>
                <InlineFieldTooltip
                  page="washing_costs"
                  section="modal_titles"
                  fieldKey="washing_cost_form"
                  fallbackText="Create or edit washing cost master rows."
                  title="Washing Cost Form"
                />
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Procedure Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.procedure_name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          procedure_name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Wash, Hi Pressure/Steam Clean"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cost per Unit *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.cost_per_unit_cad}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cost_per_unit_cad: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency *
                    </label>
                    <select
                      required
                      value={formData.currency}
                      onChange={(e) =>
                        setFormData({ ...formData, currency: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CAD">CAD</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unit of Measure *
                    </label>
                    <select
                      required
                      value={formData.unit_of_measure}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          unit_of_measure: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="sqft">sqft (Square Feet)</option>
                      <option value="sqm">sqm (Square Meters)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Converted Cost ({defaultSystemSettings.currency}/
                      {defaultSystemSettings.uom})
                    </label>
                    <input
                      type="text"
                      value={convertedCost || ""}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                      placeholder="Auto-calculated"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Automatically converted to default system currency and UOM
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sort Order *
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      required
                      value={formData.sort_order}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          sort_order:
                            e.target.value === ""
                              ? ""
                              : parseInt(e.target.value, 10),
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 0, 1, 2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Lower numbers appear first. Integers only.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.year}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          year: parseInt(e.target.value, 10) || currentYear,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={String(currentYear)}
                    />
                  </div>
                  <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_default}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_default: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Set as Default Procedure
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        (Only one procedure can be default)
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_active: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-5">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingCost ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showCopyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
            <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-2">
              <span>Copy To Year</span>
              <InlineFieldTooltip
                page="washing_costs"
                section="modal_titles"
                fieldKey="copy_to_year"
                fallbackText="Copy washing costs from one year to another."
                title="Copy To Year"
              />
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Copy all washing costs from <strong>{copySourceYear}</strong> to a
              new year.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Year
            </label>
            <input
              type="number"
              value={copyTargetYear}
              onChange={(e) => {
                setCopyTargetYear(e.target.value);
                setCopyError("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. 2027"
            />
            {copyError && (
              <p className="mt-2 text-sm text-red-600">{copyError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (copying) return;
                  setShowCopyModal(false);
                  setCopySourceYear(null);
                  setCopyTargetYear("");
                  setCopyError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={copying}
                onClick={handleConfirmCopy}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {copying ? "Copying..." : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WashingCostsManagement;
